import {
  type ReactNode,
  type Ref,
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import type {
  FieldValues,
  SubmitHandler,
  UseFormReturn,
} from "@leagueplatform/web-common";
import { FormProvider } from "@leagueplatform/web-common";
import {
  HeadingText,
  StackLayout,
  TextAction,
  Button,
  VisuallyHidden,
} from "@leagueplatform/genesis-core";
import { toastActions, TOAST_STATUS } from "@leagueplatform/toast-messages";
import type { ProfileFieldValues } from "types/profile-field-content.types";
import { useIntl } from "@leagueplatform/locales";
import { ResponsiveButtonRow } from "components/responsive-button-row/responsive-button-row.component";
import { useIsBelowTablet } from "hooks/use-is-below-tablet.hook";
import { useDirtyFormHandler } from "hooks/use-dirty-form-handler.hook";
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from "@leagueplatform/analytics";
import { ProfileFieldContent } from "components/profile-field-content/profile-field-content.component";
import { ExitWarning } from "components/modals/exit-warning/exit-warning.component";

export interface FormContainerHandle {
  submit: () => Promise<void>;
}

interface FormContainerProps<TValues extends FieldValues> {
  defaultValues: TValues;
  heading: string;
  profileFieldValues: ProfileFieldValues;
  children: ReactNode;
  formMethods: UseFormReturn<TValues>;
  onSubmit: SubmitHandler<TValues>;
  onError?: () => void;
  onSuccess?: () => void;
  isFormDirty?: boolean;
}

const FormContainerInner = <TValues extends FieldValues>(
  {
    heading,
    profileFieldValues,
    children,
    formMethods,
    onSubmit,
    onError,
    onSuccess,
    defaultValues,
    isFormDirty,
  }: FormContainerProps<TValues>,
  ref: Ref<FormContainerHandle>
) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [isOpenExitWarning, setIsOpenExitWarning] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const isBelowTablet = useIsBelowTablet();
  const {
    reset,
    getValues,
    formState: { isDirty: isDirtyRHF },
  } = formMethods;

  // Use passed prop or RHF tracked value
  const isDirty = typeof isFormDirty !== "undefined" ? isFormDirty : isDirtyRHF;

  // Track dirty forms (with unsaved changes) for ExitWarning
  const { addDirtyForm, removeDirtyForm } = useDirtyFormHandler(heading);

  useEffect(() => {
    if (isDirty) {
      addDirtyForm();
    } else {
      removeDirtyForm();
    }
  }, [isDirty, addDirtyForm, removeDirtyForm]);

  // Return focus to the edit button that opened the form
  const [closedEditing, setClosedEditing] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (editButtonRef?.current && closedEditing) {
      editButtonRef.current.focus();
    }
  }, [closedEditing]);

  const openForm = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.PERSONAL_INFO,
      detail: `edit ${heading.toLocaleLowerCase()}`,
    });
    reset(defaultValues);
    setIsEditing(true);
    setClosedEditing(false);
  };

  const closeForm = useCallback(() => {
    setIsEditing(false);
    setClosedEditing(true);
  }, []);

  const confirmExitWithoutSaving = useCallback(() => {
    setIsOpenExitWarning(false);
    removeDirtyForm();
    closeForm();
    reset(defaultValues);
  }, [removeDirtyForm, closeForm, reset, defaultValues]);

  const cancelEdit = useCallback(() => {
    if (isDirty) {
      setIsOpenExitWarning(true);
      return;
    }
    confirmExitWithoutSaving();
  }, [isDirty, confirmExitWithoutSaving]);

  const submitHandler = useCallback(
    async (values: TValues) => {
      try {
        setIsLoading(true);
        await onSubmit(values);
        if (onSuccess) {
          onSuccess();
        }
        toastActions.add({
          type: TOAST_STATUS.SUCCESS,
          text: formatMessage(
            { id: "STR_FORM_SUCCESS" },
            { formName: heading }
          ),
        });
        setIsLoading(false);
        closeForm();
      } catch (error) {
        if (onError) {
          onError();
        }
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          text: formatMessage({ id: "STR_FORM_ERROR" }, { formName: heading }),
        });
        setIsLoading(false);
      }
    },
    [closeForm, onError, onSuccess, onSubmit, formatMessage, heading]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => submitHandler(getValues()),
    }),
    [submitHandler, getValues]
  );

  return (
    <>
      <StackLayout
        horizontalAlignment="stretch"
        css={{
          backgroundColor: "$surfaceBackgroundPrimary",
          padding: "$two $oneAndHalf",
          borderWidth: "$thin",
          borderStyle: "solid",
          borderColor: "$onSurfaceBorderSubdued",
          borderRadius: "$large",
        }}
      >
        <StackLayout
          horizontalAlignment="spaceBetween"
          orientation="horizontal"
          verticalAlignment="center"
          css={{ marginBlockEnd: "$oneAndHalf" }}
        >
          <HeadingText size="lg" level="2">
            {heading}
          </HeadingText>
          <TextAction
            as="button"
            icon="interfaceEdit"
            onClick={() => (isEditing ? cancelEdit() : openForm())}
            ref={editButtonRef}
            size="medium"
          >
            {formatMessage({ id: isEditing ? "STR_CANCEL_EDIT" : "STR_EDIT" })}
            <VisuallyHidden>{heading}</VisuallyHidden>
          </TextAction>
        </StackLayout>
        {isEditing ? (
          // Form with Submit and Cancel buttons
          <FormProvider onSubmit={submitHandler} formMethods={formMethods}>
            {children}
            <ResponsiveButtonRow
              css={{
                marginBlockStart: "$oneAndHalf",
              }}
              primaryBtn={
                <Button
                  type="submit"
                  disabled={!isDirty || isLoading}
                  width={isBelowTablet ? "fillContainer" : "hugContents"}
                  loading={isLoading}
                >
                  {formatMessage({ id: "STR_SAVE_CHANGES" })}
                </Button>
              }
              secondaryBtn={
                <Button
                  priority="secondary"
                  disabled={isLoading}
                  onClick={cancelEdit}
                  width={isBelowTablet ? "fillContainer" : "hugContents"}
                >
                  {formatMessage({ id: "STR_CANCEL_EDIT" })}
                </Button>
              }
            />
          </FormProvider>
        ) : (
          // Read-only component with existing values
          <ProfileFieldContent profileFieldValues={profileFieldValues} />
        )}
      </StackLayout>
      <ExitWarning
        open={isOpenExitWarning}
        onStay={() => setIsOpenExitWarning(false)}
        onProceed={confirmExitWithoutSaving}
      />
    </>
  );
};

export const FormContainer = forwardRef(FormContainerInner) as <
  TValues extends FieldValues
>(
  props: FormContainerProps<TValues> & { ref?: Ref<FormContainerHandle> }
) => ReturnType<typeof FormContainerInner>;
